import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { ContentPane } from 'src/components/layout/content-pane'
import { Button } from 'src/components/button'
import { Spinner } from 'src/components/spinner'
import { Switch } from 'src/components/switch'
import { useToast } from 'src/components/toast'
import { organizationQueries } from 'src/services/user/queries'
import { organizationApi } from 'src/services/user/api'
import { organizationFeatureConfigApi } from 'src/services/organization-feature-config/api'
import { organizationFeatureConfigQueries } from 'src/services/organization-feature-config/queries'
import { usePermissions } from 'src/hooks/use-permissions'
import { CustomSelect } from 'src/components/custom-select'
import { SelectOption } from 'src/services/base-model'
import type { OrganizationFeatureConfig } from 'src/services/organization-feature-config/models'
import { MembersSection } from 'src/components/members/members-section'
import { RolesSection } from 'src/components/members/roles-section'
import { BillingSection } from 'src/components/billing/billing-section'
import { BotTokensSection } from 'src/components/bot-tokens/bot-tokens-section'
import { ConnectAssistantSection } from 'src/components/mcp/connect-assistant-section'
import { WhatsAppNumbersSection } from 'src/components/numbers/whatsapp-numbers-section'
import { CommsSetupWizard } from 'src/components/numbers/comms-setup-wizard'
import { UserTokensSection } from 'src/components/user-tokens/user-tokens-section'
import { DefaultOrganizationSection } from 'src/components/default-organization-section'
import { cn } from 'src/utils/style'
import { InfoHint } from 'src/components/info-hint'

type SettingsTab = 'organization' | 'account'

const COUNTRY_OPTIONS: SelectOption[] = [
  { label: 'CO - Colombia', value: 'CO' },
  { label: 'US - United States', value: 'US' },
  { label: 'MX - Mexico', value: 'MX' },
  { label: 'BR - Brazil', value: 'BR' },
  { label: 'AR - Argentina', value: 'AR' },
  { label: 'CL - Chile', value: 'CL' },
  { label: 'PE - Peru', value: 'PE' },
  { label: 'CA - Canada', value: 'CA' },
  { label: 'GB - United Kingdom', value: 'GB' },
  { label: 'DE - Germany', value: 'DE' },
  { label: 'FR - France', value: 'FR' },
  { label: 'ES - Spain', value: 'ES' },
  { label: 'JP - Japan', value: 'JP' },
  { label: 'CN - China', value: 'CN' },
  { label: 'KR - South Korea', value: 'KR' },
  { label: 'IN - India', value: 'IN' },
  { label: 'AU - Australia', value: 'AU' },
  { label: 'CH - Switzerland', value: 'CH' },
]

const CURRENCY_OPTIONS: SelectOption[] = [
  { label: 'USD - US Dollar', value: 'USD' },
  { label: 'EUR - Euro', value: 'EUR' },
  { label: 'GBP - British Pound', value: 'GBP' },
  { label: 'COP - Colombian Peso', value: 'COP' },
  { label: 'MXN - Mexican Peso', value: 'MXN' },
  { label: 'BRL - Brazilian Real', value: 'BRL' },
  { label: 'CAD - Canadian Dollar', value: 'CAD' },
  { label: 'ARS - Argentine Peso', value: 'ARS' },
  { label: 'CLP - Chilean Peso', value: 'CLP' },
  { label: 'PEN - Peruvian Sol', value: 'PEN' },
  { label: 'JPY - Japanese Yen', value: 'JPY' },
  { label: 'CNY - Chinese Yuan', value: 'CNY' },
  { label: 'CHF - Swiss Franc', value: 'CHF' },
  { label: 'AUD - Australian Dollar', value: 'AUD' },
  { label: 'KRW - South Korean Won', value: 'KRW' },
  { label: 'INR - Indian Rupee', value: 'INR' },
]

// Only boolean-valued config keys can back a toggle Switch — this excludes
// string fields like `aiSystemPrompt` that also live on the config.
type BooleanFeatureKey = {
  [K in keyof OrganizationFeatureConfig]: OrganizationFeatureConfig[K] extends boolean ? K : never
}[keyof OrganizationFeatureConfig]

type FeatureRow = {
  key: BooleanFeatureKey
  nameKey: string
  descriptionKey: string
  costType: 'free' | 'usage-based'
  betaOnly?: boolean
}

const FEATURE_ROWS: FeatureRow[] = [
  {
    key: 'emailNotificationsEnabled',
    nameKey: 'orgSettings.features.emailNotifications',
    descriptionKey: 'orgSettings.features.emailNotificationsDescription',
    costType: 'free',
  },
  {
    key: 'whatsappNotificationsEnabled',
    nameKey: 'orgSettings.features.whatsappNotifications',
    descriptionKey: 'orgSettings.features.whatsappNotificationsDescription',
    costType: 'usage-based',
  },
  {
    key: 'aiReceiptScanningEnabled',
    nameKey: 'orgSettings.features.aiReceiptScanning',
    descriptionKey: 'orgSettings.features.aiReceiptScanningDescription',
    costType: 'usage-based',
  },
  {
    key: 'aiStatementReconciliationEnabled',
    nameKey: 'orgSettings.features.aiStatementReconciliation',
    descriptionKey: 'orgSettings.features.aiStatementReconciliationDescription',
    costType: 'usage-based',
  },
  {
    key: 'aiEmbeddingsEnabled',
    nameKey: 'orgSettings.features.aiEmbeddings',
    descriptionKey: 'orgSettings.features.aiEmbeddingsDescription',
    costType: 'usage-based',
    betaOnly: true,
  },
  {
    key: 'aiVectorSearchEnabled',
    nameKey: 'orgSettings.features.aiVectorSearch',
    descriptionKey: 'orgSettings.features.aiVectorSearchDescription',
    costType: 'usage-based',
    betaOnly: true,
  },
  {
    key: 'aiQueryBuilderEnabled',
    nameKey: 'orgSettings.features.aiQueryBuilder',
    descriptionKey: 'orgSettings.features.aiQueryBuilderDescription',
    costType: 'usage-based',
    betaOnly: true,
  },
]

export const OrganizationSettings = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab: SettingsTab = searchParams.get('tab') === 'account' ? 'account' : 'organization'
  const setActiveTab = (tab: SettingsTab) => {
    setSearchParams(tab === 'account' ? { tab } : {}, { replace: true })
  }
  const { data: organizations, isLoading } = useQuery(organizationQueries.list())

  // Track which org is selected; defaults to the first org once data loads
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

  // Allow deep-linking to a specific org (e.g. from chat's no-number state), so
  // the page opens on that org rather than always the first one.
  const requestedOrgId = searchParams.get('organization')

  // Initialise selectedOrgId once the org list is available, honouring an
  // ?organization= deep link when it names one of the user's orgs.
  useEffect(() => {
    if (!organizations?.results || organizations.results.length === 0 || selectedOrgId !== null) {
      return
    }
    const requested = requestedOrgId
      ? organizations.results.find((org) => org.id === requestedOrgId)
      : undefined
    setSelectedOrgId(requested?.id ?? organizations.results[0].id)
  }, [organizations, selectedOrgId, requestedOrgId])

  // Derive the active org from the selected id
  const organization = useMemo(() => {
    if (!organizations?.results) return undefined
    if (selectedOrgId) {
      return organizations.results.find((org) => org.id === selectedOrgId)
    }
    return organizations.results[0]
  }, [organizations, selectedOrgId])

  // Build org options for the selector (only relevant when there are 2+ orgs)
  const orgOptions: SelectOption[] = useMemo(() => {
    if (!organizations?.results) return []
    return organizations.results.map((org) => ({ label: org.name, value: org.id }))
  }, [organizations])

  const selectedOrgOption: SelectOption[] = useMemo(() => {
    if (!organization) return []
    return [{ label: organization.name, value: organization.id }]
  }, [organization])

  const hasMultipleOrgs = (organizations?.results?.length ?? 0) > 1

  const { hasPermission, orgsWithPermission } = usePermissions()
  const canEditOrg = hasPermission('organization.edit')
  const { toast } = useToast()

  const { data: featureConfig } = useQuery(
    organizationFeatureConfigQueries.retrieve(organization?.id),
  )

  const updateFeature = useMutation({
    mutationFn: (data: Partial<OrganizationFeatureConfig>) => {
      if (!organization) throw new Error('No organization found')
      return organizationFeatureConfigApi.patch(organization.id, data)
    },
    onMutate: async (data) => {
      if (!organization) return
      await queryClient.cancelQueries({
        queryKey: organizationFeatureConfigQueries.all(),
      })
      const previous = queryClient.getQueryData(
        organizationFeatureConfigQueries.retrieve(organization.id).queryKey,
      )
      queryClient.setQueryData(
        organizationFeatureConfigQueries.retrieve(organization.id).queryKey,
        (old: OrganizationFeatureConfig | undefined) => (old ? { ...old, ...data } : old),
      )
      return { previous }
    },
    onSuccess: (_data, variables) => {
      const toggledKey = Object.keys(variables)[0] as keyof OrganizationFeatureConfig
      const newValue = variables[toggledKey]
      toast({
        description: newValue
          ? t('orgSettings.features.featureEnabled')
          : t('orgSettings.features.featureDisabled'),
        type: 'success',
      })
    },
    onError: (_error, _variables, context) => {
      if (context?.previous && organization) {
        queryClient.setQueryData(
          organizationFeatureConfigQueries.retrieve(organization.id).queryKey,
          context.previous,
        )
      }
      toast({
        description: t('orgSettings.features.updateError'),
        type: 'error',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: organizationFeatureConfigQueries.all(),
      })
    },
  })

  // Org-wide AI instruction text. Reuses the feature-config endpoint/query — no
  // new API. Empty string is valid and clears the org's global instructions.
  const [aiInstructions, setAiInstructions] = useState('')
  useEffect(() => {
    setAiInstructions(featureConfig?.aiSystemPrompt ?? '')
  }, [organization?.id, featureConfig?.aiSystemPrompt])

  const aiInstructionsChanged = aiInstructions !== (featureConfig?.aiSystemPrompt ?? '')

  // The org instructions are only ever sent to the receipt-extraction and
  // statement-reconciliation agents, so the editor is meaningless (and hidden)
  // unless at least one of those AI features is on. AI Search is gated separately.
  const aiGenerationEnabled = Boolean(
    featureConfig?.aiReceiptScanningEnabled || featureConfig?.aiStatementReconciliationEnabled,
  )

  const updateAiInstructions = useMutation({
    mutationFn: (value: string) => {
      if (!organization) throw new Error('No organization found')
      return organizationFeatureConfigApi.patch(organization.id, { aiSystemPrompt: value })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationFeatureConfigQueries.all() })
      toast({ description: t('orgSettings.aiInstructions.saved'), type: 'success' })
    },
    onError: () => {
      toast({ description: t('orgSettings.features.updateError'), type: 'error' })
    },
  })

  const [selectedCurrency, setSelectedCurrency] = useState<SelectOption[]>([])
  const [selectedCountry, setSelectedCountry] = useState<SelectOption[]>([])
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Sync selectedCurrency whenever the active org changes
  useEffect(() => {
    if (organization?.defaultCurrency) {
      const match = CURRENCY_OPTIONS.find((opt) => opt.value === organization.defaultCurrency)
      if (match) {
        setSelectedCurrency([match])
      } else {
        // Handle currency codes not in our predefined list
        setSelectedCurrency([
          { label: organization.defaultCurrency, value: organization.defaultCurrency },
        ])
      }
    } else {
      setSelectedCurrency([])
    }
  }, [organization?.id, organization?.defaultCurrency])

  // Deep links like #connect-assistant (from AssistantHint) target sections that
  // only render once the org has loaded, so scroll after it does.
  const { hash } = useLocation()
  useEffect(() => {
    if (!hash || !organization) return
    document.getElementById(hash.slice(1))?.scrollIntoView?.({ behavior: 'smooth' })
  }, [hash, organization])

  // Sync selectedCountry whenever the active org changes
  useEffect(() => {
    if (organization?.country) {
      const match = COUNTRY_OPTIONS.find((opt) => opt.value === organization.country)
      if (match) {
        setSelectedCountry([match])
      } else {
        setSelectedCountry([{ label: organization.country, value: organization.country }])
      }
    } else {
      setSelectedCountry([])
    }
  }, [organization?.id, organization?.country])

  const updateOrg = useMutation({
    mutationFn: async (data: { defaultCurrency?: string; country?: string | null }) => {
      if (!organization) throw new Error('No organization found')
      return organizationApi.update({
        id: organization.id,
        ...data,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueries.all() })
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    },
    onError: (error) => {
      console.error('Failed to update organization:', error)
    },
  })

  const handleOrgChange = (values: SelectOption[]) => {
    if (values.length > 0) {
      setSelectedOrgId(values[0].value)
      setSaveSuccess(false)
    }
  }

  const handleCurrencyChange = (values: SelectOption[]) => {
    setSelectedCurrency(values)
  }

  const handleSave = () => {
    const data: { defaultCurrency?: string; country?: string | null } = {}
    if (selectedCurrency.length > 0) {
      data.defaultCurrency = selectedCurrency[0].value
    }
    data.country = selectedCountry.length > 0 ? selectedCountry[0].value : null
    updateOrg.mutate(data)
  }

  const hasChanges = useMemo(() => {
    if (!organization) return false
    const currencyChanged =
      selectedCurrency.length > 0 && selectedCurrency[0].value !== organization.defaultCurrency
    const currentCountry = organization.country ?? null
    const selectedCountryValue = selectedCountry.length > 0 ? selectedCountry[0].value : null
    const countryChanged = selectedCountryValue !== currentCountry
    return currencyChanged || countryChanged
  }, [selectedCurrency, selectedCountry, organization])

  const organizationTab = isLoading ? (
    <div className="flex items-center justify-center py-12">
      <Spinner size="lg" />
    </div>
  ) : !organization ? (
    <div className="rounded-lg border border-border bg-surface p-6 text-center text-text-placeholder">
      {t('orgSettings.noOrgFound')}
    </div>
  ) : (
    <div className="space-y-6">
      {/* Org selector — a picker for multi-org users, a static label otherwise.
          One selection governs the whole page (billing, members, roles). */}
      <div
        className="rounded-lg border border-border bg-surface p-6 shadow-sm"
        data-testid="org-selector-card"
      >
        <div className="mb-4">
          <h3 className="text-lg font-medium text-text-primary">
            {t('orgSettings.orgSelector')}
            <InfoHint
              text={
                hasMultipleOrgs ? t('orgSettings.orgSelectorHint') : t('orgSettings.orgSingleHint')
              }
              className="ml-1.5"
            />
          </h3>
        </div>
        <div className="max-w-sm">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t('orgSettings.orgSelector')}
          </label>
          {hasMultipleOrgs ? (
            <CustomSelect
              options={orgOptions}
              values={selectedOrgOption}
              onChange={handleOrgChange}
              placeholder={t('orgSettings.selectOrganization')}
              searchable
              multi={false}
              clearable={false}
            />
          ) : (
            <p className="text-sm font-medium text-text-primary" data-testid="org-static-label">
              {organization.name}
            </p>
          )}
        </div>
      </div>

      {/* Default Currency Card */}
      <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-text-primary">
            {t('orgSettings.defaultCurrency')}
            <InfoHint text={t('orgSettings.defaultCurrencyHint')} className="ml-1.5" />
          </h3>
          {hasMultipleOrgs && (
            <p className="mt-1 text-sm font-medium text-gray-700">
              {t('orgSettings.currentlyConfiguring', { name: organization.name })}
            </p>
          )}
        </div>

        <div className="max-w-sm">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t('orgSettings.currency')}
          </label>
          <CustomSelect
            options={CURRENCY_OPTIONS}
            values={selectedCurrency}
            onChange={handleCurrencyChange}
            placeholder={t('orgSettings.selectCurrency')}
            searchable
            multi={false}
            clearable={false}
          />
        </div>
      </div>

      {/* Country Card */}
      <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-text-primary">
            {t('orgSettings.country')}
            <InfoHint text={t('orgSettings.countryHint')} className="ml-1.5" />
          </h3>
        </div>

        <div className="max-w-sm">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t('orgSettings.country')}
          </label>
          <CustomSelect
            options={COUNTRY_OPTIONS}
            values={selectedCountry}
            onChange={(values) => setSelectedCountry(values)}
            placeholder={t('orgSettings.selectCountry')}
            searchable
            multi={false}
            clearable
          />
        </div>
      </div>

      {/* Features */}
      <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-text-primary">
            {t('orgSettings.features.title')}
            <InfoHint text={t('orgSettings.features.description')} className="ml-1.5" />
          </h3>
        </div>

        <div className="divide-y divide-border">
          {FEATURE_ROWS.filter((feature) => !feature.betaOnly || organization?.isBeta).map(
            (feature) => {
              // Vector search requires embeddings. When embeddings is off, the vector
              // toggle is disabled and shows a hint pointing at AI Embeddings.
              const vectorGuarded =
                feature.key === 'aiVectorSearchEnabled' && !featureConfig?.aiEmbeddingsEnabled
              return (
                <div
                  key={feature.key}
                  className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-primary">
                        {t(feature.nameKey)}
                      </span>
                      <span
                        className={
                          feature.costType === 'free'
                            ? 'rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700'
                            : 'rounded-full bg-accent-50 px-2 py-0.5 text-xs font-medium text-accent-600'
                        }
                      >
                        {feature.costType === 'free'
                          ? t('orgSettings.features.free')
                          : t('orgSettings.features.usageBased')}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-text-placeholder">
                      {t(feature.descriptionKey)}
                    </p>
                    {vectorGuarded && (
                      <p
                        className="mt-0.5 text-xs text-text-tertiary"
                        data-testid="ai-vector-search-needs-embeddings"
                      >
                        {t('orgSettings.features.aiVectorSearchNeedsEmbeddings')}
                      </p>
                    )}
                    {!canEditOrg && (
                      <p className="mt-0.5 text-xs text-text-tertiary">
                        {t('orgSettings.features.managedByAdmin')}
                      </p>
                    )}
                  </div>
                  <Switch
                    data-testid={`feature-toggle-${feature.key}`}
                    checked={(featureConfig?.[feature.key] ?? false) && !vectorGuarded}
                    onCheckedChange={(checked) => {
                      // Turning embeddings off also clears vector search — vector
                      // cannot stay on without its prerequisite.
                      if (feature.key === 'aiEmbeddingsEnabled' && !checked) {
                        updateFeature.mutate({
                          aiEmbeddingsEnabled: false,
                          aiVectorSearchEnabled: false,
                        })
                        return
                      }
                      updateFeature.mutate({ [feature.key]: checked })
                    }}
                    disabled={!canEditOrg || updateFeature.isPending || vectorGuarded}
                  />
                </div>
              )
            },
          )}
        </div>
      </div>

      {/* Custom AI instructions — org-wide system-prompt text. Gated to owner/admin
          for editing, and hidden entirely when no AI generation feature is enabled. */}
      {aiGenerationEnabled && (
        <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-text-primary">
              {t('orgSettings.aiInstructions.title')}
              <InfoHint text={t('orgSettings.aiInstructions.hint')} className="ml-1.5" />
            </h3>
            {!canEditOrg && (
              <p className="mt-0.5 text-xs text-text-tertiary">
                {t('orgSettings.features.managedByAdmin')}
              </p>
            )}
          </div>

          <textarea
            data-testid="ai-instructions-input"
            className="w-full rounded-md border bg-surface p-2 text-text-primary focus:ring-2 focus:ring-border-strong disabled:cursor-not-allowed disabled:opacity-50"
            rows={5}
            value={aiInstructions}
            onChange={(e) => setAiInstructions(e.target.value)}
            disabled={!canEditOrg}
            readOnly={!canEditOrg}
            placeholder={t('orgSettings.aiInstructions.placeholder')}
          />

          {canEditOrg && (
            <div className="mt-3">
              <Button
                variant="primary"
                onClick={() => updateAiInstructions.mutate(aiInstructions)}
                disabled={!aiInstructionsChanged || updateAiInstructions.isPending}
                data-testid="ai-instructions-save"
              >
                {updateAiInstructions.isPending
                  ? t('common.saving')
                  : t('orgSettings.aiInstructions.save')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || updateOrg.isPending}
        >
          {updateOrg.isPending ? t('common.saving') : t('orgSettings.saveChanges')}
        </Button>

        {saveSuccess && (
          <span className="text-sm text-green-600">{t('orgSettings.settingsUpdated')}</span>
        )}

        {updateOrg.isError && (
          <span className="text-sm text-error">{t('orgSettings.saveError')}</span>
        )}
      </div>

      {/* Members Section */}
      {organization && <MembersSection orgId={organization.id} orgName={organization.name} />}

      {/* Roles Section */}
      {organization && <RolesSection orgId={organization.id} />}

      {/* Billing Section — only for members holding billing.access in this org */}
      {organization && orgsWithPermission('billing.access').includes(organization.id) && (
        <BillingSection orgId={organization.id} />
      )}

      {/* Connect AI assistant — visible to every member */}
      {organization && <ConnectAssistantSection orgId={organization.id} />}

      {/* Bot Tokens Section */}
      {organization && <BotTokensSection orgId={organization.id} />}

      {/* Guided messaging setup — walks the admin from no number to ready */}
      <CommsSetupWizard orgId={organization.id} />

      {/* WhatsApp Numbers Section */}
      <WhatsAppNumbersSection orgId={organization.id} />
    </div>
  )

  return (
    <>
      <PageHeader title={t('sidebar.settings')} />
      <MainContent>
        <ContentPane>
          <div role="tablist" className="mb-6 flex gap-2 border-b border-border">
            {(['organization', 'account'] as const).map((tab) => (
              <button
                key={tab}
                role="tab"
                aria-selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
                className={cn([
                  '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary',
                ])}
                data-testid={`settings-tab-${tab}`}
              >
                {t(`orgSettings.tabs.${tab}`)}
              </button>
            ))}
          </div>
          {activeTab === 'organization' ? (
            organizationTab
          ) : (
            <div className="space-y-6">
              <DefaultOrganizationSection />
              <UserTokensSection />
            </div>
          )}
        </ContentPane>
      </MainContent>
    </>
  )
}
